import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { FaShip, FaTruck, FaGlobeAmericas, FaBoxes } from "react-icons/fa";

const Counts = () => {
  const [counts, setCounts] = useState({
    shipments: 0,
    landFreight: 0,
    countries: 0,
    transport: 0,
  });

  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  const targets = {
    shipments: 50000,
    landFreight: 12000,
    countries: 25,
    transport: 125,
  };

  const startAnimation = useCallback(() => {
    if (hasAnimated) return;

    setHasAnimated(true);

    // Animate each counter
    const duration = 2000; // 2 seconds
    const step = 16; // ~60fps
    const steps = duration / step;

    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounts({
        shipments: Math.min(
          Math.floor(targets.shipments * progress),
          targets.shipments,
        ),
        landFreight: Math.min(
          Math.floor(targets.landFreight * progress),
          targets.landFreight,
        ),
        countries: Math.min(
          Math.floor(targets.countries * progress),
          targets.countries,
        ),
        transport: Math.min(
          Math.floor(targets.transport * progress),
          targets.transport,
        ),
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        // Ensure final values are exact
        setCounts({
          shipments: targets.shipments,
          landFreight: targets.landFreight,
          countries: targets.countries,
          transport: targets.transport,
        });
      }
    }, step);

    return () => clearInterval(interval);
  }, [
    hasAnimated,
    targets.countries,
    targets.landFreight,
    targets.shipments,
    targets.transport,
  ]);

  useEffect(() => {
    const currentSection = sectionRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasAnimated) {
          startAnimation();
        }
      },
      { threshold: 0.3 },
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [hasAnimated, startAnimation]);

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "k";
    }
    return num.toString();
  };

  return (
    <TheCounts ref={sectionRef}>
      <div className="counts-container">
        <div className="count-card">
          <div className="icon-wrapper">
            <FaShip />
          </div>
          <div className="count-number">{formatNumber(counts.shipments)}+</div>
          <div className="count-label">Global Shipments</div>
        </div>

        <div className="count-card">
          <div className="icon-wrapper">
            <FaTruck />
          </div>
          <div className="count-number">
            {formatNumber(counts.landFreight)}+
          </div>
          <div className="count-label">Land Freight Miles</div>
        </div>

        <div className="count-card">
          <div className="icon-wrapper">
            <FaGlobeAmericas />
          </div>
          <div className="count-number">{counts.countries}+</div>
          <div className="count-label">Countries Served</div>
        </div>

        <div className="count-card">
          <div className="icon-wrapper">
            <FaBoxes />
          </div>
          <div className="count-number">{counts.transport}+</div>
          <div className="count-label">Transport Units</div>
        </div>
      </div>
    </TheCounts>
  );
};

const TheCounts = styled.div`
  width: 100%;
  background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
  padding: 4rem 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 50%,
      rgba(212, 175, 55, 0.05) 0%,
      transparent 50%
    );
    pointer-events: none;
  }

  .counts-container {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
    position: relative;
    z-index: 1;
  }

  .count-card {
    text-align: center;
    padding: 2rem 1rem;
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    transition: all 0.3s ease;
    border: 1px solid rgba(212, 175, 55, 0.15);

    &:hover {
      transform: translateY(-5px);
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(212, 175, 55, 0.3);

      .icon-wrapper {
        transform: scale(1.1);
        background: rgba(212, 175, 55, 0.2);
      }
    }
  }

  .icon-wrapper {
    font-size: 2.5rem;
    color: #d4af37;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
    display: inline-block;
    padding: 0.75rem;
    background: rgba(212, 175, 55, 0.1);
    border-radius: 60px;
    line-height: 1;
  }

  .count-number {
    font-size: 2.8rem;
    font-weight: 700;
    color: white;
    margin-bottom: 0.5rem;
    letter-spacing: -1px;
    font-family: "Arial", sans-serif;
  }

  .count-label {
    font-size: 0.95rem;
    color: #d4af37;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 500;
  }

  @media screen and (max-width: 1366px) {
    .counts-container {
      gap: 1.5rem;
      max-width: 1100px;
    }

    .count-number {
      font-size: 2.5rem;
    }
  }

  @media screen and (max-width: 1200px) {
    padding: 3.5rem 1.5rem;

    .counts-container {
      gap: 1.25rem;
    }

    .count-number {
      font-size: 2.2rem;
    }

    .count-label {
      font-size: 0.85rem;
    }
  }

  @media screen and (max-width: 900px) {
    padding: 3rem 1.5rem;

    .counts-container {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
      max-width: 600px;
    }

    .count-card {
      padding: 1.5rem 1rem;
    }

    .count-number {
      font-size: 2rem;
    }

    .icon-wrapper {
      font-size: 2rem;
    }
  }

  @media screen and (max-width: 600px) {
    padding: 2.5rem 1.25rem;

    .counts-container {
      gap: 1rem;
    }

    .count-card {
      padding: 1.25rem 0.75rem;
    }

    .count-number {
      font-size: 1.6rem;
    }

    .count-label {
      font-size: 0.75rem;
      letter-spacing: 0.5px;
    }

    .icon-wrapper {
      font-size: 1.6rem;
      padding: 0.5rem;
      margin-bottom: 0.75rem;
    }
  }

  @media screen and (max-width: 480px) {
    .counts-container {
      grid-template-columns: 1fr;
      gap: 1rem;
      max-width: 280px;
    }

    .count-card {
      padding: 1.5rem;
    }

    .count-number {
      font-size: 2rem;
    }
  }

  @media screen and (max-width: 350px) {
    padding: 2rem 1rem;

    .counts-container {
      max-width: 260px;
    }
  }
`;

export default Counts;
